import { Injectable, NgZone } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SecureSessionService } from '../services/secure-session.service';
import { environment } from '../../environments/environment';

/** Public API path segments that should NOT receive the JWT Authorization header */
const PUBLIC_PATH_SEGMENTS = [
  'login',
  'register',
  'forgot-password',
  'reset-password',
  'resend-credentials',
  'create-applicant',
  'check-status',
  'get-all-schools',
];

/** External hosts intentionally called without the main app JWT */
const PUBLIC_HOST_PATTERNS = [
  'gds.edship.in',
  'maps.googleapis.com',
  'maps.gstatic.com',
  // Nora Print Agent on the counter PC (raw dot-matrix printing). It is a local
  // helper, not our backend, so it must never receive the session JWT.
  '127.0.0.1:9110',
];

/**
 * A 401 should only end the session when it comes from OUR OWN backend
 * (environment.apiUrl). Cross-service calls — HRMS, library, asset, etc. — run
 * on different hosts and may validate JWTs with a different signing key (notably
 * when only the main backend is run locally), so their 401s must NOT force-logout.
 */
function isPrimaryBackendRequest(url: string): boolean {
  try {
    const targetHost = new URL(url, window.location.origin).host;
    const apiHost = new URL(environment.apiUrl).host;
    return targetHost === apiHost;
  } catch {
    return false;
  }
}

function isPublicRequest(url: string): boolean {
  const lowerUrl = url.toLowerCase();
  if (PUBLIC_HOST_PATTERNS.some(host => lowerUrl.includes(host))) {
    return true;
  }

  // Parse safely for absolute and relative URLs.
  const parsed = new URL(url, window.location.origin);
  const segments = parsed.pathname
    .toLowerCase()
    .split('/')
    .filter(Boolean);

  // Match path segments exactly ("register" should not match "payregisters").
  return PUBLIC_PATH_SEGMENTS.some(segment => segments.includes(segment));
}

@Injectable()
export class JwtAuthInterceptor implements HttpInterceptor {
  constructor(
    private session: SecureSessionService,
    private router: Router,
    private dialog: MatDialog,
    private ngZone: NgZone
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip public / auth endpoints only when exact path segments match.
    const isPublic = isPublicRequest(request.url);

    if (isPublic) {
      return next.handle(request);
    }

    const token = this.session.getToken();

    if (token) {
      const cloned = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
      return next.handle(cloned).pipe(
        catchError((error: HttpErrorResponse) => {
          // Only our own backend's 401 ends the session; third-party 401s (e.g. HRMS
          // with a different signing key) must not log the user out.
          if (error.status === 401 && isPrimaryBackendRequest(request.url)) {
            // Token expired or invalid — clear session, close any open dialogs, and redirect to login.
            // Report preferences are intentionally preserved so they can be
            // restored when the user authenticates again.
            this.ngZone.run(() => {
              this.session.clearSession();
              this.dialog.closeAll();
              this.router.navigate(['/login']);
            });
          }
          return throwError(() => error);
        })
      );
    }

    return next.handle(request);
  }
}
